import React, { useCallback, useMemo } from 'react';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import {
  Content,
  ContentHeader,
  Header,
  Page,
  Progress,
  ResponseErrorPanel,
  SupportButton,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntitySearchBar,
  EntityTagPicker,
  UserListPicker,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import type { TemplateListPageProps } from '@backstage/plugin-scaffolder/alpha';
import { TemplateCard } from '@backstage/plugin-scaffolder-react/alpha';
import { Grid, Typography } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import { useArtaEdition } from '../artaEdition';
import { templateEntityRef, useTemplateAccess } from './useTemplateAccess';

type TemplateEntity = Parameters<
  NonNullable<TemplateListPageProps['templateFilter']>
>[0];

interface ArtaTemplateGroupsProps {
  groups: Array<{
    title: string;
    filter: (entity: TemplateEntity) => boolean;
  }>;
  templateFilter: (entity: TemplateEntity) => boolean;
  TemplateCardComponent?: TemplateListPageProps['TemplateCardComponent'];
  onTemplateSelected: (entity: TemplateEntity) => void;
}

const ArtaTemplateGroups = ({
  groups,
  templateFilter,
  TemplateCardComponent,
  onTemplateSelected,
}: ArtaTemplateGroupsProps) => {
  const { entities, loading, error } = useEntityList();

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const templates = entities.filter(
    entity => entity.kind.toLocaleLowerCase('en-US') === 'template',
  ) as TemplateEntity[];
  const visibleTemplates = templates.filter(templateFilter);

  if (visibleTemplates.length === 0) {
    return <Typography>No approved templates match the current filters.</Typography>;
  }

  return (
    <>
      {groups.map(group => {
        const groupedTemplates = visibleTemplates.filter(group.filter);
        if (groupedTemplates.length === 0) return null;
        return (
          <section key={group.title}>
            <Typography variant="h5" gutterBottom>
              {group.title}
            </Typography>
            <Grid container spacing={3}>
              {groupedTemplates.map(template => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={stringifyEntityRef(template)}
                >
                  {TemplateCardComponent ? (
                    <TemplateCardComponent template={template} />
                  ) : (
                    <TemplateCard
                      template={template}
                      onSelected={onTemplateSelected}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          </section>
        );
      })}
    </>
  );
};

export const ArtaTemplateListPage = (props: TemplateListPageProps) => {
  const { edition, awsEnabled, azureEnabled } = useArtaEdition();
  const { access, allowedTemplateRefs, error } = useTemplateAccess();
  const navigate = useNavigate();
  const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);

  const groups = useMemo(
    () => [
      {
        title: 'Environment Providers',
        filter: (entity: TemplateEntity) =>
          (entity.metadata.tags?.includes('environment-provider') &&
            (edition === 'multicloud' ||
              (azureEnabled && entity.metadata.tags?.includes('azure')) ||
              (awsEnabled && entity.metadata.tags?.includes('aws')))) ??
          false,
      },
      {
        title: 'Environments',
        filter: (entity: TemplateEntity) =>
          (azureEnabled &&
            entity.metadata.tags?.includes('azure-environment')) ||
          (awsEnabled && entity.metadata.tags?.includes('aws-environment')) ||
          false,
      },
      {
        title: 'Applications',
        filter: (entity: TemplateEntity) =>
          (azureEnabled && entity.metadata.tags?.includes('azure-app')) ||
          (awsEnabled &&
            entity.metadata.tags?.includes('aws') &&
            !entity.metadata.tags?.includes('environment-provider') &&
            !entity.metadata.tags?.includes('aws-environment') &&
            !entity.metadata.tags?.includes('aws-resource')) ||
          false,
      },
      {
        title: 'Resources',
        filter: (entity: TemplateEntity) =>
          (azureEnabled && entity.metadata.tags?.includes('azure-resource')) ||
          (awsEnabled && entity.metadata.tags?.includes('aws-resource')) ||
          false,
      },
    ],
    [edition, awsEnabled, azureEnabled],
  );

  const templateFilter = useCallback(
    (entity: TemplateEntity) =>
      Boolean(
        (!access?.enforced ||
          access.adminBypass ||
          allowedTemplateRefs.has(templateEntityRef(entity))) &&
          (!props.templateFilter || props.templateFilter(entity)),
      ),
    [access, allowedTemplateRefs, props.templateFilter],
  );

  const onTemplateSelected = useCallback(
    (template: TemplateEntity) => {
      const { namespace, name } = parseEntityRef(stringifyEntityRef(template));
      navigate(templateRoute({ namespace, templateName: name }));
    },
    [navigate, templateRoute],
  );

  if (error) return <ResponseErrorPanel error={error} />;
  if (!access) return <Progress />;

  return (
    <EntityListProvider>
      <Page themeId="home">
        <Header
          title="Create a new component"
          subtitle="Choose an approved architecture template"
          {...props.headerOptions}
        />
        <Content>
          <ContentHeader title="Available templates">
            <SupportButton>
              Select a template approved for your project and team.
            </SupportButton>
          </ContentHeader>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntitySearchBar />
              <EntityKindPicker initialFilter="template" hidden />
              <UserListPicker
                initialFilter="all"
                availableFilters={['all', 'starred']}
              />
              <EntityTagPicker />
              <EntityOwnerPicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <ArtaTemplateGroups
                groups={groups}
                templateFilter={templateFilter}
                TemplateCardComponent={props.TemplateCardComponent}
                onTemplateSelected={onTemplateSelected}
              />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </Content>
      </Page>
    </EntityListProvider>
  );
};
